import {getBrowserClient} from "@/lib/supabase";
import type {Database} from "@sakura-grp/database/types";
import {
  createActivity as createActivityService,
  cancelActivity as cancelActivityService,
  updateActivityDate as updateActivityDateService,
  listActivities as listActivitiesService,
  getActivityCounts,
  ACTIVITY_THRESHOLDS,
} from "@sakura-grp/database/services";

export type ActivityType = "phone" | "message" | "visit" | "letter";

export type Activity = Database["public"]["Tables"]["activities"]["Row"];

export interface CreateActivityInput {
  store_id: string;
  customer_id: string;
  hostess_id: string;
  activity_type: ActivityType;
  activity_date?: string;
  notes?: string | null;
}

export interface ActivityStats {
  phone_count: number;
  message_count: number;
  visit_count: number;
  letter_count: number;
  total_count: number;
  sales_status: "good" | "poor";
}

/**
 * 営業活動を記録する（即座に保存）
 */
export async function createActivity(
  input: CreateActivityInput,
): Promise<Activity> {
  const supabase = getBrowserClient();
  return await createActivityService(supabase, input);
}

/**
 * 営業活動一覧を取得
 */
export async function fetchActivities(params: {
  customer_id?: string;
  hostess_id?: string;
  store_id?: string;
  limit?: number;
}): Promise<Activity[]> {
  const supabase = getBrowserClient();
  const {data} = await listActivitiesService(supabase, {
    customer_id: params.customer_id,
    hostess_id: params.hostess_id,
    store_id: params.store_id,
    limit: params.limit,
  });
  return data as unknown as Activity[];
}

/**
 * 営業活動一覧を取得（関連データ含む）
 * NOTE: hostess固有のselectカラム（status, role）があるため共通サービスは使用しない
 */
export async function fetchActivitiesWithRelations(params: {
  hostess_id?: string;
  store_id?: string;
  limit?: number;
}) {
  const supabase = getBrowserClient();

  let query = supabase
    .from("activities")
    .select(`
      *,
      customer:customers(id, name, company_name),
      store:stores(id, store_name),
      hostess:hostesses!activities_hostess_id_fkey(id, name, status, role)
    `)
    .is("cancelled_at", null)
    .order("activity_date", {ascending: false})
    .order("created_at", {ascending: false});

  if (params.hostess_id) {
    query = query.eq("hostess_id", params.hostess_id);
  }
  if (params.store_id) {
    query = query.eq("store_id", params.store_id);
  }
  if (params.limit) {
    query = query.limit(params.limit);
  }

  const {data, error} = await query;

  if (error) {
    console.error("[fetchActivitiesWithRelations] Database error:", error);
    throw new Error("営業活動の取得に失敗しました。しばらく待ってから再度お試しください。");
  }

  return data || [];
}

/**
 * 営業統計を取得（直近N日間）
 */
export async function fetchActivityStats(params: {
  customer_id: string;
  hostess_id: string;
  days?: number;
}): Promise<ActivityStats> {
  const supabase = getBrowserClient();

  const counts = await getActivityCounts(supabase, {
    customer_id: params.customer_id,
    hostess_id: params.hostess_id,
    days: params.days,
  });

  return {
    ...counts,
    sales_status: ACTIVITY_THRESHOLDS.evaluate(counts.total_count),
  };
}

/**
 * 営業活動をキャンセル（イミュータブル: キャンセルのみ許可）
 */
export async function cancelActivity(
  id: string,
  hostessId: string,
  cancelReason?: string,
): Promise<void> {
  const supabase = getBrowserClient();
  await cancelActivityService(supabase, {
    id,
    cancel_reason: cancelReason,
    cancelled_by: hostessId,
  });
}

/**
 * 営業活動の活動日時 (activity_date) を訂正する
 * 変更履歴は DB トリガーで activity_date_history に自動記録される
 */
export async function updateActivityDate(
  id: string,
  activityDate: string,
): Promise<void> {
  const supabase = getBrowserClient();
  await updateActivityDateService(supabase, {
    id,
    activity_date: activityDate,
  });
}
